/**
 * Natural-language search intent.
 *
 * Turns "asiatique rapide sans porc" into structured filters. Runs only on
 * Enter, never per keystroke — the local scorer already handles typing, and
 * this is a network call.
 *
 * Everything here is best-effort: a failure returns null and the caller keeps
 * whatever the local parser produced. Search must never depend on this
 * resolving, and must never show the user an error because it didn't.
 */

import { trackLLMGeneration } from './analyticsService';
import { getAuthHeaders } from './apiClient';
import { foldText } from '../utils/searchNormalize';
import { DISH_TYPE_ALIASES } from '../constants/dishTypes';
import { getApiBaseUrl } from '../config/apiBase';

/**
 * Give up and keep the local result rather than leave the user waiting.
 *
 * Measured 0.4-1.5s against gemini-2.5-flash-lite with thinking disabled; this
 * leaves headroom for a serverless cold start without stranding the user.
 */
const TIMEOUT_MS = 4000;

export interface SearchIntent {
  /** Free-text terms to match against titles and ingredients. */
  include: string[];
  /** Terms whose presence disqualifies a recipe. */
  exclude: string[];
  maxMinutes: number | null;
  diet: 'veg' | 'fish' | null;
  dishType: string | null;
  minRating: number | null;
}

const DISH_TYPES = Object.keys(DISH_TYPE_ALIASES);

/**
 * Note what this deliberately does not ask for: a `cuisine` field.
 *
 * Every recipe in the catalog has an empty `cuisines` array, so a cuisine enum
 * would be a filter over data that does not exist. Cuisine and mood are instead
 * expressed as `include` terms ("asiatique" → miso, teriyaki, ramen...) which
 * match real title and ingredient text.
 */
const PROMPT = (query: string) => `You convert a cook's natural-language recipe search into structured filters.

Return ONLY a JSON object with exactly these keys:
{
  "include": [],
  "exclude": [],
  "maxMinutes": null,
  "diet": null,
  "dishType": null,
  "minRating": null
}

Rules:
- include: words likely to appear in a matching recipe's title or ingredients.
  Expand concepts into concrete cooking words. The recipe collection is mostly
  French with some English, so give BOTH languages where it helps.
  "asiatique" -> ["asiatique","asian","miso","teriyaki","ramen","soja","sesame","curry"]
  "reconfortant" -> ["mijote","gratin","soupe","braise","ragout","stew","chili"]
  "leger" -> ["salade","salad","legumes","vapeur","grille"]
  Never echo an abstract adjective back ("reconfortant", "impressionnant",
  "leger") - always translate it into concrete dish or ingredient words.
- exclude: ingredients the user does not want. "sans porc" -> ["porc","pork","bacon","jambon"]
- maxMinutes: integer if the user implies speed ("rapide" -> 30, "en 15 minutes" -> 15), else null.
- diet: "veg" for vegetarian/vegan, "fish" for fish/seafood, else null.
- dishType: one of ${JSON.stringify(DISH_TYPES)} if the user clearly names a course, else null.
- minRating: 4.5 if the user asks for highly rated / best / impressive, else null.
- Use null, not a guess. An empty include list is fine if the query is only filters.

Examples:
Query: "asiatique rapide sans porc"
{"include":["asiatique","asian","miso","teriyaki","ramen","soja","sesame"],"exclude":["porc","pork","bacon","jambon"],"maxMinutes":30,"diet":null,"dishType":null,"minRating":null}

Query: "j'ai du saumon et des epinards"
{"include":["saumon","salmon","epinards","spinach"],"exclude":[],"maxMinutes":null,"diet":null,"dishType":null,"minRating":null}

Query: "quelque chose de reconfortant pour un soir de pluie"
{"include":["mijote","gratin","soupe","braise","ragout","stew","chili"],"exclude":[],"maxMinutes":null,"diet":null,"dishType":null,"minRating":null}

Query: "un dessert impressionnant pour recevoir"
{"include":[],"exclude":[],"maxMinutes":null,"diet":null,"dishType":"dessert","minRating":4.5}

Query: "${query.replace(/"/g, '\\"')}"`;

function asStringArray(value: unknown, limit: number): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 1)
    .map((v) => foldText(v))
    .filter((v, i, all) => v.length > 1 && all.indexOf(v) === i)
    .slice(0, limit);
}

/**
 * Coerce the model's output into a `SearchIntent`.
 *
 * Everything is validated rather than trusted: an unrecognized dishType or an
 * out-of-range time becomes null instead of a filter that silently matches
 * nothing.
 */
export function parseIntentResponse(raw: string): SearchIntent | null {
  let text = raw.trim();
  const codeBlock = text.match(/```(?:json)?\s*\n?([\s\S]*?)\n?\s*```/);
  if (codeBlock) text = codeBlock[1].trim();

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const obj = parsed as Record<string, unknown>;

  const dishType =
    typeof obj.dishType === 'string' && DISH_TYPES.includes(obj.dishType)
      ? obj.dishType
      : null;

  const maxMinutes =
    typeof obj.maxMinutes === 'number' && obj.maxMinutes > 0 && obj.maxMinutes <= 480
      ? Math.round(obj.maxMinutes)
      : null;

  const diet = obj.diet === 'veg' || obj.diet === 'fish' ? obj.diet : null;

  const minRating =
    typeof obj.minRating === 'number' && obj.minRating > 0 && obj.minRating <= 5
      ? obj.minRating
      : null;

  const intent: SearchIntent = {
    include: asStringArray(obj.include, 12),
    exclude: asStringArray(obj.exclude, 12),
    maxMinutes,
    diet,
    dishType,
    minRating,
  };

  // Nothing actionable came back — treat as a miss so the caller keeps the
  // local result rather than applying a no-op filter set.
  return isEmpty(intent) ? null : intent;
}

export function isEmpty(intent: SearchIntent): boolean {
  return (
    intent.include.length === 0 &&
    intent.exclude.length === 0 &&
    intent.maxMinutes === null &&
    intent.diet === null &&
    intent.dishType === null &&
    intent.minRating === null
  );
}

/**
 * Same query, same answer, no second network call. Queries repeat constantly
 * (backspace and retype, revisit a search), so this removes most of the cost.
 */
const cache = new Map<string, SearchIntent | null>();
const MAX_CACHE_ENTRIES = 200;

export function clearIntentCache(): void {
  cache.clear();
}

export async function fetchSearchIntent(query: string): Promise<SearchIntent | null> {
  const key = foldText(query);
  if (!key) return null;
  if (cache.has(key)) return cache.get(key) ?? null;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${getApiBaseUrl()}/api/ai/search-intent`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ prompt: PROMPT(query) }),
      signal: controller.signal,
    });

    if (!response.ok) return null;

    // `response.ok` alone is not proof of delivery: the Expo web dev server
    // answers unknown /api/* paths with 200 and an HTML document.
    const data = await response.json().catch(() => null);
    if (!data || typeof data.result !== 'string') return null;

    const intent = parseIntentResponse(data.result);

    trackLLMGeneration({
      $ai_trace_id: Math.random().toString(36).slice(2) + Date.now().toString(36),
      $ai_span_name: 'search_intent',
      $ai_model: data.model,
      $ai_provider: 'google',
      $ai_input_tokens: data.input_tokens,
      $ai_output_tokens: data.output_tokens,
      $ai_latency: data.latency_s,
    });

    if (cache.size >= MAX_CACHE_ENTRIES) cache.clear();
    cache.set(key, intent);
    return intent;
  } catch {
    // Timeout, offline, aborted, malformed — all the same to the caller.
    return null;
  } finally {
    clearTimeout(timeout);
  }
}
